import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import slugify from "slugify";

const useResolveGlossaryText = (markdown) => {
  const renderers = {
    h1: ({ children }) => <h1>{children}</h1>,
    h2: ({ children }) => {
      const textToSlugify = typeof children[0] === "string" ? children : "";
      return (
        <h2
          id={slugify(textToSlugify, { lower: true })}
          className="text-h2 topic-title text-fw-medium"
        >
          {textToSlugify}
        </h2>
      );
    },
    h3: ({ children }) => (
      <h3 className="text-h3 gap-2x text-clr-primary text-fw-medium">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-h4 gap-1x text-clr-primary text-fw-medium">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="text-clr-primary text-fw-medium">{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className="text-clr-primary text-fw-medium">{children}</h6>
    ),
    p: ({ children }) => (
      <p className="text-p1 text-fw-regular text-clr-secondary gap-2x">
        {children}
      </p>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-clr-blue"
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="rich-text-table gap-2x-v2">
        <table>
          <tbody>{children}</tbody>
        </table>
      </div>
    ),
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <td>
        <p className="text-p1 text-fw-regular text-clr-primary gap-2x">
          <b>{children}</b>
        </p>
      </td>
    ),
    td: ({ children }) => (
      <td>
        <p className="text-p1 text-fw-regular text-clr-primary gap-2x">
          {children}
        </p>
      </td>
    ),
    code: ({ inline, className, children }) => {
      <pre className="code-block">
        <code className="inline-code">{children}</code>
      </pre>;
    },
    img: ({ src, alt }) => <img src={src} alt={alt} className="gap-4x" />,
  };

  const markdownText =
    typeof markdown === "string" ? markdown : markdown?.data || "";

  const renderedContent = (
    <ReactMarkdown
      components={renderers}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
    >
      {markdownText}
    </ReactMarkdown>
  );

  const toc = markdownText
    .split("\n")
    .filter((line) => line.startsWith("## "))
    .map((heading, index) => {
      const text = heading.replace("## ", "").trim();
      return (
        <div className="toc-item" key={index}>
          <a
            href={`#${slugify(text, { lower: true })}`}
            className="text-h6 text-fw-medium text-clr-primary font--primary"
          >
            {index + 1}. <span className="toc-item-name">{text}</span>
          </a>
        </div>
      );
    });

  return {
    toc,
    content: renderedContent,
  };
};

export default useResolveGlossaryText;
